import { ID_BASED_FILE_CONNECTORS } from 'js/constants/connectionCategories';
import cloneDeep from 'lodash/cloneDeep';
import filter from 'lodash/filter';
import forEach from 'lodash/forEach';
import has from 'lodash/has';
import isArray from 'lodash/isArray';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import isPlainObject from 'lodash/isPlainObject';
import map from 'lodash/map';
import mapKeys from 'lodash/mapKeys';
import orderBy from 'lodash/orderBy';
import sortBy from 'lodash/sortBy';
import union from 'lodash/union';
import {
  LOGOUT_COMPLETE,
  PROBE_CALL_SOAP_FAILURE,
  PROBE_CALL_SOAP_SUCCESS,
  PROBE_CLEAR_ALL,
  PROBE_CLEAR_FILES,
  PROBE_GET_FILE_FAILURE,
  PROBE_GET_FILE_SUCCESS,
  PROBE_GET_KAFKA_TOPICS_FAILURE,
  PROBE_GET_KAFKA_TOPICS_SUCCESS,
  PROBE_GET_KAFKA_TOPIC_SAMPLE_FAILURE,
  PROBE_GET_KAFKA_TOPIC_SAMPLE_SUCCESS,
  PROBE_GET_SAMPLE_FAILURE,
  PROBE_GET_SAMPLE_SUCCESS,
  PROBE_GET_TREE_FAILURE,
  PROBE_GET_TREE_REQUEST,
  PROBE_GET_TREE_SUCCESS,
  PROBE_GET_WSDL_FAILURE,
  PROBE_GET_WSDL_SUCCESS,
  PROBE_REMOVE_REST_STAGE,
  PROBE_TOGGLE_NODE,
} from '../../constants/actionTypes';
import JSONUtils from '../../lib/JSONUtils';
import UIUtils from '../../lib/UIUtils';

const initialState = {};
const DEFAULT_PROBE_TREE_STRUCTURE = {
  tree: {},
  nestedProbeTree: [],
  error: null,
  treeLastUpdatedAt: null,
  files: {},
};

export const probeReducer = (state = initialState, action) => {
  switch (action.type) {
    case PROBE_GET_TREE_REQUEST:
      return processGetTreeRequest(state, action);

    case PROBE_GET_TREE_SUCCESS:
      return processGetTreeSuccess(state, action);

    case PROBE_GET_TREE_FAILURE:
      return processGetTreeError(state, action);

    case PROBE_GET_WSDL_SUCCESS:
      return processGetWSDLSuccess(state, action);

    case PROBE_GET_WSDL_FAILURE:
      return processGetWSDLError(state, action);

    case PROBE_GET_KAFKA_TOPICS_SUCCESS:
      return processGetKafkaTopicsSuccess(state, action);

    case PROBE_GET_KAFKA_TOPICS_FAILURE:
      return processGetKafkaTopicsError(state, action);

    case PROBE_CALL_SOAP_SUCCESS:
      return processCallSOAPSuccess(state, action);

    case PROBE_CALL_SOAP_FAILURE:
      return processCallSOAPError(state, action);

    case PROBE_GET_FILE_SUCCESS:
      return processGetFileSuccess(state, action);

    case PROBE_GET_FILE_FAILURE:
      return processGetFileError(state, action);

    case PROBE_GET_KAFKA_TOPIC_SAMPLE_SUCCESS:
      return processGetKafkaTopicSampleSuccess(state, action);

    case PROBE_GET_KAFKA_TOPIC_SAMPLE_FAILURE:
      return processGetKafkaTopicSampleError(state, action);

    case PROBE_TOGGLE_NODE:
      return toggleTreeNode(state, action.payload);

    case PROBE_GET_SAMPLE_SUCCESS:
      return probeGetSampleSuccess(state, action);

    case PROBE_GET_SAMPLE_FAILURE:
      return probeGetSampleFailure(state, action);

    case PROBE_CLEAR_ALL:
      return probeClearAll(state, action);

    case PROBE_REMOVE_REST_STAGE:
      return probeRemoveRestStage(state, action);

    case PROBE_CLEAR_FILES:
      return probeClearFiles(state, action);

    case LOGOUT_COMPLETE:
      return initialState;

    default:
      return state;
  }
};

export default probeReducer;

function processGetTreeRequest(state, action) {
  const {
    requestInfo: { key, id },
  } = action;
  const loadingKey = key || id;

  return {
    ...state,
    loading: {
      ...state.loading,
      [loadingKey]: true,
    },
  };
}

function processGetTreeSuccess(state, action) {
  const {
    requestInfo: { info, id, key, connectionType },
    result: { output },
  } = action;
  const stateForThisResource = has(state, `${id}.tree`) ? state[id] : DEFAULT_PROBE_TREE_STRUCTURE;
  const newProbeTree = cloneDeep(stateForThisResource.tree);
  let outputProbeSample = DEFAULT_PROBE_TREE_STRUCTURE;

  // ignore the "meta" file at the root
  if (isPlainObject(output) && has(output, 'meta')) {
    let { meta, ...filteredOutput } = output;
    addTreeToState(filteredOutput || {}, newProbeTree, connectionType);
  } else {
    addTreeToState(output || {}, newProbeTree, connectionType);
  }

  const scannedNodePath = info && info.path;
  if (newProbeTree[scannedNodePath]) {
    const scannedNode = newProbeTree[scannedNodePath];
    scannedNode.scanned = true;
    scannedNode.open = true;
    scannedNode.error = null;

    // When a deeply nested node is scanned, expand all its ancestors.
    // This enables direct path navigation without iterative probe calls.
    for (let parent = scannedNode.parent; parent; parent = newProbeTree[parent]?.parent) {
      if (newProbeTree[parent]) {
        newProbeTree[parent].open = true;
      }
    }
  }

  // if loading tree for the first time, auto-expand first node
  if (isEmpty(stateForThisResource.tree)) {
    const rootNodes = filter(newProbeTree, { parent: null });

    if (rootNodes.length === 1) {
      rootNodes[0].open = true;
    }
  }

  const hasPreviousFileLoaded = state[id]?.files !== undefined;
  const files = hasPreviousFileLoaded ? state[id].files : DEFAULT_PROBE_TREE_STRUCTURE.files;

  outputProbeSample = {
    ...outputProbeSample,
    files,
    tree: newProbeTree,
    nestedProbeTree: newProbeTree ? UIUtils.unflattenTree(newProbeTree) : null,
    error: null,
    treeLastUpdatedAt: Date.now(),
  };

  return {
    ...state,
    [id]: outputProbeSample,
    loading: {
      ...state.loading,
      [key || id]: false,
    },
  };
}

function processGetTreeError(state, action) {
  let {
    requestInfo: { info, id, key },
    error,
  } = action;
  let outputProbeSample = DEFAULT_PROBE_TREE_STRUCTURE;

  // Determine if this is a sub-path probe (e.g. probing a specific folder/path)
  // vs. a root probe (e.g. initial connection test or credential validation).
  // Sub-path probes have `info` with a `path` property set.
  // Root probe failures indicate a connection-level issue and should surface the
  // "Try Again" overlay. Sub-path failures (e.g. path doesn't exist) should only
  // mark the specific tree node as errored, leaving the tree browsable.
  const isSubPathProbe = !!(info && info.path);

  // For sub-path probes, preserve the existing top-level error state so the
  // directory tree remains visible. For root probes, surface the error.
  const topLevelError = isSubPathProbe ? (has(state, id) ? state[id].error : null) : error;

  let stateForThisResource = has(state, id)
    ? { ...state[id], error: topLevelError }
    : { ...DEFAULT_PROBE_TREE_STRUCTURE, tree: null, error: topLevelError };
  let newProbeTree = stateForThisResource.tree;

  if (newProbeTree) {
    newProbeTree = cloneDeep(newProbeTree);
    const scannedNodePath = info && info.path;
    if (newProbeTree[scannedNodePath]) {
      const scannedNode = newProbeTree[scannedNodePath];
      scannedNode.error = error;
    }
  }

  const hasPreviousFileLoaded = state[id]?.files !== undefined;
  const files = hasPreviousFileLoaded ? state[id].files : DEFAULT_PROBE_TREE_STRUCTURE.files;

  outputProbeSample = {
    ...outputProbeSample,
    files,
    tree: newProbeTree,
    nestedProbeTree: newProbeTree ? UIUtils.unflattenTree(newProbeTree) : null,
    error: topLevelError,
    treeLastUpdatedAt: Date.now(),
  };

  return {
    ...state,
    [id]: outputProbeSample,
    loading: {
      ...state.loading,
      [key || id]: false,
    },
  };
}

function processGetWSDLSuccess(state, action) {
  const {
    requestInfo: { id },
    result: { output },
  } = action;
  const wsdl = cloneDeep(output);

  forEach(wsdl.services, (service) => {
    forEach(service.bindings, (binding) => {
      forEach(binding.operations, (operation) => {
        const params = map(operation.parameters, (param) => ({
          ...param,
          required: param.minOccurs > 0 || !!param.required,
        }));
        operation.sortedParams = orderBy(params, ['required', 'path'], ['desc', 'asc']);
      });
    });
  });

  const stateForThisResource = has(state, id) ? state[id] : {};
  const newState = {
    ...stateForThisResource,
    wsdl,
    wsdlError: null,
    lastUpdatedAt: Date.now(),
  };

  return {
    ...state,
    [id]: newState,
  };
}

function processGetWSDLError(state, action) {
  const {
    requestInfo: { id },
    error,
  } = action;

  const stateForThisResource = has(state, id) ? state[id] : {};
  const newState = {
    ...stateForThisResource,
    wsdlError: error,
    lastUpdatedAt: Date.now(),
  };

  return {
    ...state,
    [id]: newState,
  };
}

function processGetKafkaTopicsSuccess(state, action) {
  const {
    requestInfo: { id },
    result: { output = {} },
    receivedAt,
  } = action;

  // Keep topics and subscriptions separate. Pub/Sub callers must pick the right
  // list for their context: sinks publish to a topic, sources read from a
  // subscription. The component knows which it is via its topicFieldName.
  const { topics = [], subscriptions = [], queues = [] } = output;

  const stateForThisResource = has(state, id) ? state[id] : {};
  const newState = {
    ...stateForThisResource,
    topics: sortBy(topics),
    subscriptions: !isEmpty(subscriptions) ? sortBy(subscriptions) : null,
    queues: !isEmpty(queues) ? sortBy(queues) : null,
    error: null,
    lastUpdatedAt: receivedAt,
  };

  return {
    ...state,
    [id]: newState,
  };
}

function processGetKafkaTopicsError(state, action) {
  const {
    requestInfo: { id },
    error,
    receivedAt,
  } = action;

  const stateForThisResource = has(state, id) ? state[id] : {};
  const newState = {
    ...stateForThisResource,
    topics: null,
    queues: null,
    error,
    lastUpdatedAt: receivedAt,
  };

  return {
    ...state,
    [id]: newState,
  };
}

function processCallSOAPSuccess(state, action) {
  const {
    requestInfo: { id },
    result: { output },
  } = action;

  const stateForThisResource = has(state, id) ? state[id] : {};
  const newState = {
    ...stateForThisResource,
    callResult: output,
    callError: null,
    lastUpdatedAt: Date.now(),
  };

  return {
    ...state,
    [id]: newState,
  };
}

function processCallSOAPError(state, action) {
  const {
    requestInfo: { id },
    error,
  } = action;

  const stateForThisResource = has(state, id) ? state[id] : {};
  const newState = {
    ...stateForThisResource,
    callError: error,
    lastUpdatedAt: Date.now(),
  };

  return {
    ...state,
    [id]: newState,
  };
}

function processGetFileSuccess(state, action) {
  const {
    result,
    receivedAt,
    requestInfo: { id, info, shouldFetchOnResource, resourceId, resourceType },
  } = action;
  let thisResourceId = shouldFetchOnResource === true ? `${resourceType}_${resourceId}` : id;

  let actualOutputProbe = state[thisResourceId] || {};
  return {
    ...state,
    [thisResourceId]: {
      ...actualOutputProbe,
      files: {
        ...actualOutputProbe.files,
        [info.id]: {
          ...result.output,
          lastUpdatedAt: receivedAt,
        },
      },
    },
  };
}

function processGetFileError(state, action) {
  let {
    code,
    error,
    message,
    requestInfo: { id, info, shouldFetchOnResource, resourceId, resourceType },
  } = action;
  let messageObj;
  if (code >= 500) {
    messageObj = JSONUtils.parseJson(message);
    message = messageObj ? messageObj.errorMessage : message;
  }

  let thisResourceId = shouldFetchOnResource === true ? `${resourceType}_${resourceId}` : id;

  let actualOutputProbe = state[thisResourceId] || {};
  return {
    ...state,
    [thisResourceId]: {
      ...actualOutputProbe,
      files: {
        ...actualOutputProbe.files,
        [info.id]: {
          statusCode: code,
          errorMessage: message || error,
          lastUpdatedAt: Date.now,
        },
      },
    },
  };
}

function processGetKafkaTopicSampleSuccess(state, action) {
  const {
    result: {
      output: { errorMessage: error, response, contentType },
    },
    requestInfo: { id, topic },
    receivedAt,
  } = action;
  const stateForThisResource = has(state, id) ? state[id] : {};
  const samplesForThisResource = stateForThisResource.samples || {};

  return {
    ...state,
    [id]: {
      ...stateForThisResource,
      samples: {
        ...samplesForThisResource,
        [topic]: {
          error,
          response,
          contentType,
          lastUpdatedAt: receivedAt,
        },
      },
    },
  };
}

function processGetKafkaTopicSampleError(state, action) {
  const {
    error,
    requestInfo: { id, topic },
    receivedAt,
  } = action;
  const stateForThisResource = has(state, id) ? state[id] : {};
  const samplesForThisResource = stateForThisResource.samples || {};

  return {
    ...state,
    [id]: {
      ...stateForThisResource,
      samples: {
        ...samplesForThisResource,
        [topic]: {
          error,
          response: null,
          contentType: null,
          lastUpdatedAt: receivedAt,
        },
      },
    },
  };
}

function toggleTreeNode(state, payload) {
  const { id, node } = payload;

  if (!has(state, id)) {
    return state;
  }

  let thisRecord = state[id];

  if (!has(thisRecord, 'tree')) {
    return state;
  }

  const newTree = cloneDeep(state[id].tree);
  const nodeId = node.id;
  const toggledNode = newTree[nodeId];
  toggledNode.open = !toggledNode.open;

  thisRecord = {
    ...thisRecord,
    tree: newTree,
    nestedProbeTree: newTree ? UIUtils.unflattenTree(newTree) : null,
    error: null,
    treeLastUpdatedAt: Date.now(),
  };

  return {
    ...state,
    [id]: thisRecord,
  };
}

function probeGetSampleSuccess(state, action) {
  const {
    requestInfo: { id, stageIndex },
    result,
  } = action;
  const stateForThisResource = has(state, id) ? state[id] : {};
  return {
    ...state,
    [id]: {
      ...stateForThisResource,
      [stageIndex]: {
        response: result,
        error: null,
        sampleLastUpdatedAt: Date.now(),
      },
    },
  };
}

function probeGetSampleFailure(state, action) {
  const {
    requestInfo: { id },
  } = action;
  const stateForThisResource = has(state, id) ? state[id] : {};

  return {
    ...state,
    [id]: {
      ...stateForThisResource,
      sample: {
        response: null,
        error: {
          code: action.code,
          error: action.error,
        },
        sampleLastUpdatedAt: Date.now(),
      },
    },
  };
}

// function probeClearSample(state, action) {
//   const { id } = action.payload;
//   const stateForThisResource = has(state, id) ? state[id] : {};
//
//   return {
//     ...state,
//     [id]: omit(stateForThisResource, ['sample'])
//   };
// }

function probeClearAll(state, action) {
  return initialState;
}

function probeRemoveRestStage(state, action) {
  const { id, stageIndex } = action.payload;
  const stateForThisResource = has(state, id) ? { ...state[id] } : {};

  if (has(stateForThisResource, stageIndex)) {
    // remove stage data
    delete stateForThisResource[stageIndex];
  }

  const newState = mapKeys(stateForThisResource, (value, key) => {
    const keyInt = parseInt(key, 10);

    if (keyInt > stageIndex) {
      return keyInt - 1;
    }

    return key;
  });

  return {
    ...state,
    [id]: newState,
  };
}

function probeClearFiles(state, action) {
  const { id, shouldFetchOnResource, resourceType, resourceId } = action.payload;

  let thisResourceId = shouldFetchOnResource === true ? `${resourceType}_${resourceId}` : id;

  const stateForThisResource = has(state, thisResourceId) ? state[thisResourceId] : {};

  if (stateForThisResource.files) {
    return {
      ...state,
      [thisResourceId]: {
        ...stateForThisResource,
        files: {},
      },
    };
  } else {
    return state;
  }
}

function addTreeToState(tree, state, connectionType, parentPath = null) {
  const nodePaths = [];
  const effectiveTree = isArray(tree) ? mapKeys(tree, 'name') : tree;

  for (let nodeName in effectiveTree) {
    if (isNil(nodeName)) {
      //bypass this entry if bad data.
      continue;
    }
    const treeData = effectiveTree[nodeName];
    const parentFullPath = parentPath && state[parentPath] && state[parentPath].fullPath;
    const nodePathSeparator =
      (parentFullPath && parentFullPath.endsWith('/')) || (nodeName && nodeName.startsWith('/'))
        ? ''
        : '/';
    const fullPath = `${parentFullPath ? `${parentFullPath}${nodePathSeparator}` : ''}${nodeName}`;
    const nodePath =
      ID_BASED_FILE_CONNECTORS.includes(connectionType) && connectionType !== 'sharepoint' //sharepoint still requires full path, just the id is used when display_name is available.
        ? nodeName
        : fullPath;
    nodePaths.push(nodePath);

    if (!state[nodePath]) {
      state[nodePath] = {};
    }

    const node = state[nodePath];
    node.id = nodePath;
    node.path = nodePath;
    node.fullPath = fullPath;
    node.name = nodeName;
    node.displayName = treeData.display_name || node.name;
    node.parent = parentPath;
    node.scanned = !!node.scanned;
    node.open = !!node.open;
    node.error = node.error || null;

    node.type = treeData.type;

    if (has(treeData, 'size')) {
      node.size = treeData.size;
    }

    if (has(treeData, 'mime_type')) {
      node.mimeType = treeData.mime_type;
    }

    if (has(treeData, 'created_at')) {
      node.createdAt = treeData.created_at;
    }

    if (has(treeData, 'updated_at')) {
      node.updatedAt = treeData.updated_at;
    }

    if (has(treeData, 'primaryKey')) {
      node.primaryKey = treeData.primaryKey;
    }

    if (has(treeData, 'autoIncrementColumn')) {
      node.autoIncrementColumn = treeData.autoIncrementColumn;
    }

    if (has(treeData, 'timestampColumn')) {
      node.timestampColumn = treeData.timestampColumn;
    }

    if (has(treeData, 'autoIncrement')) {
      node.autoIncrement = treeData.autoIncrement;
    }

    if (has(treeData, 'timestamp')) {
      node.timestamp = treeData.timestamp;
    }

    const parentNode = parentPath && state[parentPath];
    if (parentNode) {
      node.level = parentNode.level + 1;
    } else {
      node.level = 1;
    }

    const parentDisplayPath = parentNode && parentNode.displayPath;
    const displayPathSeparator =
      (parentDisplayPath && parentDisplayPath.endsWith('/')) ||
      (node.displayName && node.displayName.startsWith('/'))
        ? ''
        : '/';
    const displayPath = `${
      parentDisplayPath ? `${parentDisplayPath}${displayPathSeparator}` : ''
    }${node.displayName}`;

    node.displayPath = displayPath;

    if (node.type === 'file') {
      node.children = [];
      node.scanned = true;
    } else {
      const childPaths = addTreeToState(treeData.value, state, connectionType, nodePath);
      node.children = isEmpty(node.children) ? childPaths : union(node.children, childPaths);
    }
  }

  return nodePaths;
}
